import {EMPTY, fromEvent, interval, merge, of} from 'rxjs';
import {catchError, delay, exhaustMap, filter, flatMap, ignoreElements, map, switchMap, takeUntil, tap, throttleTime} from 'rxjs/operators';
import {
    CHECK_SESSION,
    CHECK_SESSION_SUCCESS,
    EXTEND_SESSION,
    LOAD_USER_PROFILE,
    LOG_IN,
    LOG_OUT,
    OPEN_USER_PROFILE_DIALOG,
    HIDE_SESSION_WARNING,
    START_HEARTBEAT,
    UPDATE_USER_PROFILE,
    UPDATE_USER_PROFILE_SUCCESS,
} from './SessionActionTypes';

import config from 'react-global-configuration';
import {combineEpics} from 'redux-observable';
import {
    errorHandlingWithLogoutCheck,
    getJson,
    putJson, // används av UPDATE_USER_PROFILE epic nedan
    postJson,
} from "../../../components/AjaxHelpers";
import {globalActions} from "../../global/redux";
import {termsActions} from "../../terms/redux";
import {sessionActions} from './';

// Heartbeat constants
const SESSION_TIMEOUT = 15 * 60 * 1000; // Must match server.servlet.session.timeout
const HEARTBEAT_INTERVAL = 60 * 1000;
const WARNING_THRESHOLD = 12 * 60 * 1000; // Show warning 3 min before timeout

export const sessionEpics = combineEpics(
    // On app start or page refresh, check if user has a valid session
    (action$, state$) => action$.ofType(CHECK_SESSION).pipe(
        switchMap(action =>
            getJson({
                url: config.get('apiBaseUrl') + '/auth/v1/me',
            }).pipe(
                map(payload => sessionActions.checkSessionSuccess(payload)),
                catchError(error => {
                    // 401 means not logged in - this is expected, not an error
                    if (error.status === 401) {
                        return [sessionActions.checkSessionFail()];
                    }
                    return [sessionActions.checkSessionFail()];
                })
            )
        )
    ),


    // Emit login event after session check success
    (action$, state$) => action$.ofType(CHECK_SESSION_SUCCESS).pipe(
        delay(500),
        switchMap(action =>
            postJson({
                url: config.get('apiBaseUrl') + '/auth/v1/login-event',
                body: {},
            }).pipe(
                map(() => ({type: 'LOGIN_EVENT_LOGGED'})),
                catchError(() => of({type: 'LOGIN_EVENT_LOG_FAILED'}))
            )
        )
    ),

    // Fetch terms acceptance status after session check success so the gate
    (action$, state$) => action$.ofType(CHECK_SESSION_SUCCESS).pipe(
        map(() => termsActions.fetchStatus())
    ),

    // Login: Just redirect to backend OAuth endpoint
    (action$, state$) => action$.ofType(LOG_IN).forEach(action => {
        // Server handles the entire OAuth flow
        window.location = config.get('apiBaseUrl').replace('/api', '') + '/oauth2/authorization/keycloak';
    }),

    // Logout: Redirect to backend logout endpoint
    (action$, state$) => action$.ofType(LOG_OUT).forEach(action => {
        // Server handles Keycloak logout and session invalidation.
        // Full page reload via backend redirect is what resets client state.
        window.location = config.get('apiBaseUrl').replace('/api', '') + '/logout';
    }),





    (action$, state$) => action$.ofType(OPEN_USER_PROFILE_DIALOG).pipe(
        flatMap(action => ([sessionActions.loadUserProfile()]))
    ),

    (action$, state$) => action$.ofType(LOAD_USER_PROFILE).pipe(
        switchMap(action =>
            getJson({
                url: config.get('apiBaseUrl') + '/me/v1/profile',
            }).pipe(
                map(payload => sessionActions.loadUserProfileSucceeded(payload)),
                catchError(error => errorHandlingWithLogoutCheck(error,
                    [sessionActions.loadUserProfileFailed(error)],
                    "Fel vid laddning av användarprofil"))
            )
        )
    ),

    (action$, state$) => action$.ofType(UPDATE_USER_PROFILE).pipe(
        switchMap(action =>
            putJson({
                url: config.get('apiBaseUrl') + '/me/v1/profile',
                body: action.userProfile,
            }).pipe(
                map(payload => sessionActions.updateUserProfileSucceeded()),
                catchError(error => errorHandlingWithLogoutCheck(error,
                    [sessionActions.updateUserProfileFailed(error)],
                    "Fel vid uppdatering av användarprofil"))
            )
        )
    ),

    (action$, state$) => action$.ofType(UPDATE_USER_PROFILE_SUCCESS).pipe(
        flatMap(action => ([globalActions.globalInfo("Profilen uppdaterades")]))
    ),

    // Start heartbeat after successful login.
    // (Tidigare filter på role !== 'CHANGE_PASSWORD' borttaget: rollen kan aldrig
    // tilldelas i nuvarande OAuth2-flöde via Keycloak.)
    (action$, state$) => action$.ofType(CHECK_SESSION_SUCCESS).pipe(
        filter(action => action.userInfo),
        map(() => sessionActions.startHeartbeat())
    ),

    // Track DOM activity, keep session alive, logout when idle
    (action$, state$) => action$.ofType(START_HEARTBEAT).pipe(
        switchMap(() => {
            let lastActivity = Date.now();

            // DOM listener, updates lastActivity
            const activity$ = merge(
                fromEvent(document, 'mousemove'),
                fromEvent(document, 'keydown'),
                fromEvent(document, 'click'),
                fromEvent(document, 'scroll'),
                fromEvent(document, 'touchstart'),
            ).pipe(
                throttleTime(1000),
                tap(() => { lastActivity = Date.now(); }),
                ignoreElements(),
            );

            // Check idle time, ping, warn or logout
            const checkSession = () => {
                const idleTime = Date.now() - lastActivity;
                const warningVisible = state$.value.session.sessionWarningVisible;

                if (idleTime >= SESSION_TIMEOUT) {
                    return of(sessionActions.logOut());
                }

                if (idleTime >= WARNING_THRESHOLD && !warningVisible) {
                    return of(sessionActions.showSessionWarning());
                }

                if (idleTime < HEARTBEAT_INTERVAL) {
                    return getJson({
                        url: config.get('apiBaseUrl') + '/auth/v1/me',
                    }).pipe(
                        map(() => ({ type: 'HEARTBEAT_PING_OK' })),
                        catchError(error => {
                            if (error.status === 401) {
                                return of(sessionActions.logOut());
                            }
                            return of({ type: 'HEARTBEAT_PING_FAILED' });
                        })
                    );
                }

                return EMPTY;
            };

            // Check session on every heartbeat
            const heartbeat$ = interval(HEARTBEAT_INTERVAL).pipe(
                exhaustMap(() => checkSession()),
            );

            // Listens for the browser tab becoming visible
            const wakeCheck$ = fromEvent(document, 'visibilitychange').pipe(
                filter(() => document.visibilityState === 'visible'),
                exhaustMap(() => checkSession()),
            );

            // Reset idle timer only after server confirms session is still valid
            const extendConfirmed$ = action$.ofType(HIDE_SESSION_WARNING).pipe(
                tap(() => { lastActivity = Date.now(); }),
                ignoreElements(),
            );

            // Merge all into one observable, switchMap manages lifecycle
            return merge(activity$, heartbeat$, wakeCheck$, extendConfirmed$).pipe(
                takeUntil(action$.ofType(LOG_OUT)),
            );
        })
    ),

    // Extend session: user clicked "Fortsätt" in warning dialog
    (action$, state$) => action$.ofType(EXTEND_SESSION).pipe(
        exhaustMap(() =>
            getJson({
                url: config.get('apiBaseUrl') + '/auth/v1/me',
            }).pipe(
                map(() => sessionActions.hideSessionWarning()),
                catchError(error => {
                    if (error.status === 401) {
                        return of(sessionActions.logOut());
                    }
                    return of(
                        sessionActions.extendSessionFailed(),
                        globalActions.globalError("Kunde inte verifiera sessionen. Försök igen eller logga in på nytt."),
                    );
                })
            )
        )
    ),
)